import { SelectedPlanStore } from '@servicetitan/financing-plans-shared';
import {
    WorkflowStore,
    useProviderConfig,
    SetupDataStore,
} from '@servicetitan/financing-workflow-shared';
import {
    ApplicationDataStore,
    DisclosureDataStore,
    getApplicationDataDefaultValues,
} from '@servicetitan/financing-application-shared';
import { ConsentDataStore } from '@servicetitan/financing-decision-shared';
import { useEffect } from 'react';

// There is no easy way to reset the Stepper, so as a workaround, this component is here.
// Its only purpose is to provide a place where WorkflowRenderer can be briefly redirected
// in order to unmount and remount the actual workflow components.
export const ResetWorkflow = () => {
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { defaultWorkflowStage } = useProviderConfig();
    const { applicationData, setApplicationData } = ApplicationDataStore.useStore();
    const { setSelectedPlan } = SelectedPlanStore.useStore();
    const { setAgreedToDisclosures } = DisclosureDataStore.useStore();
    const { setConsentData } = ConsentDataStore.useStore();
    const { setSetupData } = SetupDataStore.useStore();

    // Immediately redirect the workflow to the starting stage and clear all application data.
    useEffect(() => {
        // Reset workflow stage
        setWorkflowStage(defaultWorkflowStage);

        // Clear all user-inputted application data by resetting to initial state
        // but preserve existing customer and location data
        const defaultValues = getApplicationDataDefaultValues({
            customerFirstName: applicationData.applicant?.primaryApplicant?.firstName ?? '',
            customerLastName: applicationData.applicant?.primaryApplicant?.lastName ?? '',
            customerEmail: applicationData.applicant?.primaryApplicant?.email ?? '',
            customerPhone: applicationData.applicant?.primaryApplicant?.primaryPhone ?? '',
            serviceLocationStreet:
                applicationData.mailingAddress?.primaryApplicant?.address?.street ?? '',
            serviceLocationUnit:
                applicationData.mailingAddress?.primaryApplicant?.address?.address2 ?? '',
            serviceLocationCity:
                applicationData.mailingAddress?.primaryApplicant?.address?.city ?? '',
            serviceLocationState:
                applicationData.mailingAddress?.primaryApplicant?.address?.state ?? '',
            serviceLocationZip:
                applicationData.mailingAddress?.primaryApplicant?.address?.zip ?? '',
            source: applicationData.source,
        });
        setApplicationData(defaultValues);

        setSelectedPlan(null);
        setAgreedToDisclosures(false);
        setConsentData({ applicantConsent: undefined, coApplicantConsent: undefined });

        // Clear any pre-approval anchor so a reset workflow can't route a later regular plan to the
        // hard app. Functional merge preserves provider/tenantId/etc.
        setSetupData(prev => (prev ? { ...prev, preApprovalId: undefined } : prev));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        setWorkflowStage,
        defaultWorkflowStage,
        setApplicationData,
        setSelectedPlan,
        setAgreedToDisclosures,
        setConsentData,
        setSetupData,
        // Note: applicationData properties are intentionally omitted from dependencies
        // This effect should only run once when the component mounts to capture the current values
    ]);

    return null;
};
